import React from 'react';
import {
  BrowserRouter as Router, Route, Redirect, Switch,
} from 'react-router-dom';
import { Flex } from 'rebass/styled-components';
import Login from './Login';
import RecommendedActionsKB from './RecommendedActionsKB';
import RecommendedActionsBots from './RecommendedActionsBots';
import AuthenticatedRoute from './AuthenticatedRoute';

import CcsInit from './admin/CcsInit';
import NormalizePriorities from './admin/normalize-priorities';
import Home from './Home';
import ContextPage from './ContextPage';
import IntentPage from './IntentPage';
import CustomContextPage from './CustomContextPage';
import SdePage from './SdePage';
import RoutingPage from '../components/policy/policies-ccs-v1';
import AskMaven from './AskMaven';
import Settings from './Settings';
import AuthRedirect from './AuthRedirect';
import NoAccess from './NoAccess';
import EngagementController from './EngagementController';
import ManagePolicyPage from './ManagePolicyPage';
import DebuggerPage from './Debugger';

import { useAppState, useNotification } from '../hooks';
import { FeatureGate } from '../components';

// TODO: Move file reader logic out of routes index
const fileReader = new FileReader();

const handleFileUpload = (event) => {
  if (event.target.files.length) {
    fileReader.readAsText(event.target.files[0]);
  }
};

const isJsonString = (str) => {
  try {
    return JSON.parse(str);
  } catch (event) {
    return false;
  }
};

const setFileReaderLoad = (callback, errorCallback) => {
  fileReader.onload = () => {
    const content = fileReader.result;
    if (isJsonString(content)) {
      callback(JSON.parse(content));
    } else {
      errorCallback();
    }
  };
};

export default function RouterProps(rprops) {
  const { addNotification } = useNotification();
  const { features } = useAppState();

  const routerProps = {
    ...rprops,
    addNotification,
  };

  const IntentContext = () => (
    <IntentPage
      name="Intents"
      feature="maven.feature.contextVariables"
      {...routerProps}
    />
  );

  const UserContext = () => (
    <ContextPage
      name="Consumer"
      type="user"
      feature="maven.feature.contextVariables"
      {...routerProps}
    />
  );

  const ConversationContext = () => (
    <ContextPage
      name="Conversation"
      subtitle="Conversation context are attributes of a conversation such as time and assigned skill, that can be used in Intent and Context Policies."
      type="conversation"
      feature="maven.feature.contextVariables"
      {...routerProps}
    />
  );
  const OperationalContext = () => (
    <ContextPage
      name="Operational"
      type="operational"
      feature="maven.feature.contextVariables"
      {...routerProps}
    />
  );
  const CustomContext = () => (
    <CustomContextPage
      name="Custom"
      subtitle="Create and manage custom context attributes. These can be static, such as a list of names or emails, or can be dynamically invoked via LivePerson Functions."
      type="custom"
      feature="maven.feature.contextVariables"
      {...routerProps}
    />
  );
  const IntentPolicies = () => (
    <ManagePolicyPage
      policyName="Manage Policies"
      subtitle="Create, reorder, enable and disable policies. At run-time policies are applied from top to bottom."
      type="rules"
      {...routerProps}
    />
  );

  const SdeContext = () => (
    <SdePage
      name="Structured Data Entities (SDE)"
      subtitle="Structured Data Entities (SDE) can be used for including business, user, and context data in campaigns and engagements when conversations are initiated."
      type="sde"
      feature="maven.feature.sdePage"
      {...routerProps}
    />
  );
  const AgentPrioritization = () => (
    <RoutingPage policyName="Agent Prioritization" type="prioritization" {...routerProps} />
  );
  const QueuePrioritization = () => (
    <RoutingPage policyName="Queue Prioritization" type="prioritization" {...routerProps} />
  );

  const isAgentAssistEnabled = () => features?.['maven.agentassist.enabled'];

  return (
    <Router>
      <Flex flex={1}>
        <Switch>
          <Route
            path="/login/:accountId?"
            exact
            render={(props) => <Login {...props} {...routerProps} />}
          />
          <Route
            path="/auth-redirect"
            exact
            render={(props) => <AuthRedirect {...props} {...routerProps} />}
          />
          <Route
            path="/no-access"
            exact
            render={(props) => <NoAccess {...props} {...routerProps} />}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            path="/ccs-init"
            exact
            component={CcsInit}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            path="/normalize-priorities"
            exact
            component={NormalizePriorities}
          />
          <AuthenticatedRoute
            data-testid="authenticated-route"
            {...routerProps}
            path="/"
            exact
            component={Home}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            path="/ccs-init"
            exact
            component={CcsInit}
          />
          {isAgentAssistEnabled() && (
            <AuthenticatedRoute
              {...routerProps}
              data-testid="authenticated-route"
              feature="maven.feature.recommendedActionBots"
              path="/recommended-actions"
              exact
              component={RecommendedActionsBots}
              handleFileUpload={handleFileUpload}
              fileReader={fileReader}
              setFileReaderLoad={setFileReaderLoad}
            />
          )}
          {isAgentAssistEnabled() && (
            <AuthenticatedRoute
              {...routerProps}
              data-testid="authenticated-route"
              feature="maven.feature.recommendedActionKBs"
              path="/knowledge-base"
              exact
              component={RecommendedActionsKB}
            />
          )}
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.aiPoweredRouting"
            path="/intent-content-policies"
            exact
            component={IntentPolicies}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="isCcsV2"
            path="/policy-logs"
            exact
            component={DebuggerPage}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.aiPoweredRouting"
            path="/manage-policies"
            exact
            component={ManagePolicyPage}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.aiPoweredRouting"
            path="/agent-prioritization"
            exact
            component={AgentPrioritization}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.aiPoweredRouting"
            path="/queue-prioritization"
            exact
            component={QueuePrioritization}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.contextVariables"
            path="/user-context"
            exact
            component={UserContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.contextVariables"
            path="/intents"
            exact
            component={IntentContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.contextVariables"
            path="/conversation-context"
            exact
            component={ConversationContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.contextVariables"
            path="/operational-context"
            exact
            component={OperationalContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.contextVariables"
            path="/custom-context"
            exact
            component={CustomContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.sdePage"
            path="/sde-context"
            exact
            component={SdeContext}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.askMaven"
            path="/ask-maven"
            exact
            component={AskMaven}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            feature="maven.feature.settings"
            path="/settings"
            exact
            component={Settings}
          />
          <AuthenticatedRoute
            {...routerProps}
            data-testid="authenticated-route"
            path="/engagement-controller"
            exact
            component={EngagementController}
          />
          <Redirect to="/login" />
        </Switch>
      </Flex>
    </Router>
  );
}
